import { BaseCommand } from './BaseCommand';
import { MapCommandType } from '../types';
import { IMarkerConfig, ICoordinate } from '../../types';
import { MarkerManager } from '../../managers/MarkerManager';

export class AddMarkerCommand extends BaseCommand {
  private markerManager: MarkerManager;
  private marker: IMarkerConfig;

  constructor(markerManager: MarkerManager, marker: IMarkerConfig) {
    super(
      MapCommandType.ADD_MARKER,
      `Add marker at ${marker.coordinate.lat}, ${marker.coordinate.lng}`
    );
    this.markerManager = markerManager;
    this.marker = marker;
  }

  execute(): void {
    this.markerManager.addMarker(this.marker);
  }

  undo(): void {
    this.markerManager.removeMarker(this.marker.id);
  }

  canExecute(): boolean {
    return !this.markerManager.hasMarker(this.marker.id);
  }

  canUndo(): boolean {
    return this.markerManager.hasMarker(this.marker.id);
  }
}

export class RemoveMarkerCommand extends BaseCommand {
  private markerManager: MarkerManager;
  private marker: IMarkerConfig;

  constructor(markerManager: MarkerManager, marker: IMarkerConfig) {
    super(MapCommandType.REMOVE_MARKER, `Remove marker ${marker.id}`);
    this.markerManager = markerManager;
    this.marker = marker;
  }

  execute(): void {
    this.markerManager.removeMarker(this.marker.id);
  }

  undo(): void {
    this.markerManager.addMarker(this.marker);
  }

  canExecute(): boolean {
    return this.markerManager.hasMarker(this.marker.id);
  }

  canUndo(): boolean {
    return !this.markerManager.hasMarker(this.marker.id);
  }
}

export class UpdateMarkerCommand extends BaseCommand {
  private markerManager: MarkerManager;
  private newMarker: IMarkerConfig;
  private previousMarker: IMarkerConfig;

  constructor(
    markerManager: MarkerManager,
    newMarker: IMarkerConfig,
    previousMarker: IMarkerConfig
  ) {
    super(MapCommandType.UPDATE_MARKER, `Update marker ${newMarker.id}`);
    this.markerManager = markerManager;
    this.newMarker = newMarker;
    this.previousMarker = previousMarker;
  }

  execute(): void {
    this.markerManager.updateMarker(this.newMarker);
  }

  undo(): void {
    this.markerManager.updateMarker(this.previousMarker);
  }

  canExecute(): boolean {
    return this.markerManager.hasMarker(this.newMarker.id);
  }

  canUndo(): boolean {
    return this.markerManager.hasMarker(this.previousMarker.id);
  }
}

export class MoveMarkerCommand extends BaseCommand {
  private markerManager: MarkerManager;
  private markerId: string;
  private oldCoordinate: ICoordinate;
  private newCoordinate: ICoordinate;

  constructor(
    markerManager: MarkerManager,
    markerId: string,
    oldCoordinate: ICoordinate,
    newCoordinate: ICoordinate
  ) {
    super(
      MapCommandType.UPDATE_MARKER,
      `Move marker ${markerId} from (${oldCoordinate.lat.toFixed(6)}, ${oldCoordinate.lng.toFixed(6)}) to (${newCoordinate.lat.toFixed(6)}, ${newCoordinate.lng.toFixed(6)})`
    );
    this.markerManager = markerManager;
    this.markerId = markerId;
    this.oldCoordinate = oldCoordinate;
    this.newCoordinate = newCoordinate;
  }

  execute(): void {
    const marker = this.markerManager.getMarker(this.markerId);
    if (marker) {
      const updatedMarker = { ...marker, coordinate: this.newCoordinate };
      this.markerManager.updateMarker(updatedMarker);
    }
  }

  undo(): void {
    const marker = this.markerManager.getMarker(this.markerId);
    if (marker) {
      const revertedMarker = { ...marker, coordinate: this.oldCoordinate };
      this.markerManager.updateMarker(revertedMarker);
    }
  }

  canExecute(): boolean {
    return this.markerManager.hasMarker(this.markerId);
  }

  canUndo(): boolean {
    return this.markerManager.hasMarker(this.markerId);
  }
}
